import React from 'react';
import PropTypes from 'prop-types';
import Comment from '../input/Comment.component';
import {PopUp} from './PopUp.component';
import * as Api from 'utils/api/api';
import {TASK_ACTIONS} from 'utils/constants';
import ErrorDialog from './ErrorDialog.component';
import {connect} from 'react-redux';
import {getTaskDetails, updateTaskDetailsAfterAction} from '../taskdetails-remake/taskDetails.action';
import translate from '../translations/translations.wrapper.jsx';
import * as reduxSelectors from '../store/application.reducers';
import {mapErrorMessage} from 'utils/errorHandle.function';
import {Button, ButtonType} from "components/common/Button.component";
import {FormField} from "components/common/FormField.component";
import {TextInput} from "components/common/TextInput.component";
import { FileUploader, FileUploaderState, FileUploaderType } from "components/common/FileUploader.component";

//this is to make sure the tabbing stays inside the dialog
const TAB_INDEX = 1000;
//const file size limit 3MB
const FILE_SIZE_LIMIT = 3 * 1024 * 1024; // 3 MB;

/**
 * popup which will be used in case user has selected 'add attachment'
 * expects as props:
 * list of selected tasks
 * close popup callback
 * translate function
 */
export class AddAttachment extends PopUp {
    constructor(props) {
        super(props);
        this.state = {
            file: null,
            title: '',
            comment: '',
            actionRunning: false,
            displayError: null,
            uploadError: ""
        }
        this.titleInputRef = React.createRef();
        this.ok = this.ok.bind(this);
        this.buildContent = this.buildContent.bind(this);
        this.fileAdded = this.fileAdded.bind(this);
        this.focusFirst = this.focusFirst.bind(this);
    }

    fileAdded(file) {
        this.setState({
            file: file
        });
    }

    ok() {
        this.setState({actionRunning: true});
        const self = this;
        this.props.tasks.forEach(({key}) => {
            Api.addAttachment(key, encodeURIComponent(this.state.title), encodeURIComponent(this.state.comment), this.state.file).then(function (response) {
                    let documentName = JSON.parse(response)?.text || '';
                    self.props.updateTaskDetailsAfterAction(key, TASK_ACTIONS.ADD_ATTACHMENT);
                    self.props.showNotification(documentName + " " + self.props.translate("popUp.addAttachment.hasBeenAdded"));
                    self.props.closeCallback();
                },
                function (error) {
                    self.setState({
                        uploadError: mapErrorMessage(error),
                        actionRunning: false
                    });
                }
            );
        });

    }

    focusFirst() {
        this.titleInputRef.current?.focus();
    }

    componentDidMount() {
        super.componentDidMount();
        this.focusFirst();
    }

    buildContent() {
        const okEnabled = !this.state.actionRunning && this.state.comment && this.state.title && this.state.file && !this.state.uploadError;

        let isPDFAllowed = this.props.taskDetail.taskPermission?.allowPdfUpload === true;
        let isHTMLAllowed = this.props.taskDetail.taskPermission?.allowHtmlUpload === true;
        let isTIFFAllowed = this.props.taskDetail.taskPermission?.allowTiffUpload === true;
        let acceptedFileExtensions = isPDFAllowed ? ".png, .pdf, .jpg, .jpeg" : ".png, .jpg, .jpeg";
        let acceptedFilesHint = isPDFAllowed ? "popUp.addAttachment.browseHint" : "popUp.addAttachment.browseHintPNG";
        let acceptedFilesHintAdditionalTypes = "";
        if (isHTMLAllowed) {
            acceptedFileExtensions += ", .html";
            acceptedFilesHintAdditionalTypes += ", HTML";
        }
        if (isTIFFAllowed) {
            acceptedFileExtensions += ", .tif, .tiff";
            acceptedFilesHintAdditionalTypes += ", TIFF";
        }

        return (
            <div>
                {this.state.displayError && <ErrorDialog
                    error={this.state.displayError}
                    closeCallback={() => this.setState({actionRunning: false, displayError: null})}
                    translate={this.props.translate}
                />}
                <div className="modal-body ast_add_attachments px-0" aria-modal="true">
                    <div className="row w-100 m-0 p-0">
                        <FormField
                            id="title"
                            label={this.props.translate("popUp.addAttachment.fileTitleLabel")}
                            labelState={`(${this.props.translate("popUp.required")})`}>
                            <TextInput
                                ref={this.titleInputRef}
                                value={this.state.title}
                                onChange={(e) => this.setState({title: e.target.value})}
                                tabIndex={TAB_INDEX}
                            />
                        </FormField>
                    </div>

                    <div className="row w-100 m-0 p-0">
                        <Comment
                            inputHint={this.props.translate("popUp.approve.commentHint")}
                            inputLimit={256}
                            translate={this.props.translate}
                            tabIndex={TAB_INDEX + 1}
                            propagateValue={(value) => this.setState({comment: value})}
                            hideInitialValue={true}
                            id="comment"
                            label={this.props.translate("popUp.addAttachment.commentLabel")}
                            labelState={`(${this.props.translate("popUp.required")})`}
                        />
                    </div>

                    <div className="row w-100 m-0 p-0 pb-2">
                        <FileUploader
                            type={FileUploaderType.DEFAULT}
                            state={FileUploaderState.ENABLED}
                            header={{
                                label: this.props.translate("popUp.addAttachment.fileLabel") + ` (${this.props.translate("popUp.required")})`,
                                description: this.props.translate(acceptedFilesHint) + acceptedFilesHintAdditionalTypes
                            }}
                            acceptedFileExtensions={acceptedFileExtensions}
                            maxFileSize={FILE_SIZE_LIMIT}
                            tabIndex={TAB_INDEX + 2}
                            onFileSelect={this.fileAdded}
                            error={this.state.uploadError}
                            translate={this.props.translate}
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <div className="d-flex gap-8 float-right">
                        <Button type={ButtonType.PRIMARY}
                                onClick={this.ok}
                                disabled={!okEnabled}
                                tabIndex={TAB_INDEX + 4}
                                text={this.props.translate("popUp.ok")}
                        />
                        <Button type={ButtonType.SECONDARY}
                                onClick={this.props.closeCallback}
                                onBlur={this.focusFirst}
                                tabIndex={TAB_INDEX + 4}
                                text={this.props.translate("popUp.cancel")}
                        />
                    </div>
                </div>
            </div>
        )
            ;
    }

    getType() {
        return TASK_ACTIONS.ADD_ATTACHMENT;
    }

}

AddAttachment.propTypes = {
    tasks: PropTypes.array, // selected tasks
    closeCallback: PropTypes.func, //callback for closing the popup
    translate: PropTypes.func, // to translate texts
}

const withTranslations = translate(AddAttachment);
const mapStateToProps = function (store) {
    return {
        taskDetail: reduxSelectors.getTaskDetails(store)
    }
};

const connected = connect(mapStateToProps, {
    getTaskDetails,
    updateTaskDetailsAfterAction
})(withTranslations);

export default connected;
